/**
 * CSV Import/Export Module
 *
 * Handles importing and exporting server profiles via CSV format using Papa Parse.
 * Includes robust validation and error reporting.
 * Integrated with server database for validation and templates.
 */

import Papa from 'papaparse';
import type { ServerProfile } from '../types/servers';
import { ALL_SERVERS, getServerById, getAllServerIds } from '../data/servers';
import { serverProfileSchema } from '../schemas/servers.schema';
import type { ValidationError, ValidationWarning } from '../types/validation';

/**
 * Import result type
 */
export interface ImportResult<T> {
  success: boolean;
  data?: T;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

/**
 * CSV Column Headers for simple server profile format
 * Defines the expected column names for server profile CSV
 */
export const CSV_HEADERS_SIMPLE = [
  'serverId',
  'name',
  'vendor',
  'model',
  'role',
  'gpuPlatformId',
  'gpusPerNode',
  'cpuModel',
  'cpuSockets',
  'memoryGB',
  'powerWatts',
  'rackUnits',
  'coolingType',
  'notes',
];

/**
 * CSV Column Headers for detailed server profile format
 */
export const CSV_HEADERS_DETAILED = [
  'id',
  'name',
  'role',
  'vendor',
  'model',
  'gpuPlatformId',
  'gpusPerNode',
  'cpuModel',
  'cpuSockets',
  'cpuCoresPerSocket',
  'cpuTdpPerSocket',
  'memoryTotalGB',
  'memoryType',
  'memorySpeedMTs',
  'powerMaxWatts',
  'powerTypicalWatts',
  'powerIdleWatts',
  'psuCount',
  'psuCapacityWatts',
  'rackUnits',
  'widthMm',
  'depthMm',
  'heightMm',
  'weightKg',
  'coolingType',
  'maxTempC',
  'notes',
];

/**
 * Required fields that must be present and non-empty
 */
const REQUIRED_FIELDS = [
  'name',
  'vendor',
  'gpuType',
  'gpusPerNode',
  'powerWatts',
  'ruHeight',
];

/**
 * Numeric fields that must be valid numbers
 */
const NUMERIC_FIELDS = [
  'gpusPerNode',
  'cpuCount',
  'memoryGb',
  'storageTb',
  'pciSlots',
  'powerWatts',
  'ruHeight',
  'ibPortsPerNode',
  'nsPortsPerNode',
  'dpusPerNode',
  'dpuOobPorts',
];

/**
 * Parse CSV string into server profiles
 *
 * @param csvData - CSV string content
 * @returns Import result with parsed profiles or errors
 */
export function parseCSV(csvData: string): ImportResult<ServerProfile[]> {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];
  const profiles: ServerProfile[] = [];

  // Handle empty input
  if (!csvData || csvData.trim().length === 0) {
    errors.push({
      message: 'CSV data is empty',
      severity: 'critical',
    });
    return { success: false, errors, warnings };
  }

  // Parse CSV using Papa Parse
  const parseResult = Papa.parse<Record<string, string>>(csvData, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim(),
    transform: (value) => value.trim(),
  });

  // Check for parsing errors
  if (parseResult.errors && parseResult.errors.length > 0) {
    parseResult.errors.forEach((error) => {
      errors.push({
        message: `CSV parsing error: ${error.message}`,
        row: error.row,
        severity: 'error',
      });
    });
  }

  // Validate headers - check for either simple or detailed format
  const headers = parseResult.meta.fields || [];
  const hasSimpleHeaders = CSV_HEADERS_SIMPLE.some((h) => headers.includes(h));
  const hasDetailedHeaders = CSV_HEADERS_DETAILED.some((h) => headers.includes(h));

  if (!hasSimpleHeaders && !hasDetailedHeaders) {
    errors.push({
      message: `Invalid CSV format. Expected columns for simple format: ${CSV_HEADERS_SIMPLE.slice(0, 5).join(', ')}... or detailed format: ${CSV_HEADERS_DETAILED.slice(0, 5).join(', ')}...`,
      severity: 'critical',
    });
    return { success: false, errors, warnings };
  }

  // Process each row
  parseResult.data.forEach((row, index) => {
    const rowNum = index + 2; // +2 for 1-based indexing and header row
    const rowErrors: ValidationError[] = [];
    const rowWarnings: ValidationWarning[] = [];

    // Check required fields
    REQUIRED_FIELDS.forEach((field) => {
      if (!row[field] || row[field].length === 0) {
        rowErrors.push({
          field,
          message: `Required field '${field}' is missing or empty`,
          row: rowNum,
          severity: 'error',
        });
      }
    });

    // Validate numeric fields
    NUMERIC_FIELDS.forEach((field) => {
      const value = row[field];
      if (value && value.length > 0) {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          rowErrors.push({
            field,
            message: `Field '${field}' must be a valid number, got '${value}'`,
            row: rowNum,
            severity: 'error',
          });
        } else if (numValue < 0) {
          rowErrors.push({
            field,
            message: `Field '${field}' must be non-negative`,
            row: rowNum,
            severity: 'error',
          });
        }
      }
    });

    // Business logic validations
    if (row.gpusPerNode) {
      const gpus = Number(row.gpusPerNode);
      if (!isNaN(gpus) && gpus > 16) {
        rowWarnings.push({
          field: 'gpusPerNode',
          message: `Unusually high GPU count: ${gpus}`,
          row: rowNum,
          suggestion: 'Typical servers have 1-16 GPUs. Verify this value.',
        });
      }
    }

    if (row.powerWatts) {
      const power = Number(row.powerWatts);
      if (!isNaN(power) && power > 20000) {
        rowWarnings.push({
          field: 'powerWatts',
          message: `Unusually high power consumption: ${power}W`,
          row: rowNum,
          suggestion: 'Typical servers consume 1-20kW. Verify this value.',
        });
      }
    }

    if (row.ruHeight) {
      const ru = Number(row.ruHeight);
      if (!isNaN(ru) && ru > 12) {
        rowWarnings.push({
          field: 'ruHeight',
          message: `Unusually tall server: ${ru}U`,
          row: rowNum,
          suggestion: 'Typical servers are 1-12U. Verify this value.',
        });
      }
    }

    // If no errors, try to load server from database by ID
    if (rowErrors.length === 0) {
      try {
        // First check if serverId is provided (preferred method)
        if (row.serverId || row.id) {
          const serverId = row.serverId || row.id;
          const server = getServerById(serverId);
          if (server) {
            profiles.push(server);
          } else {
            rowErrors.push({
              message: `Server ID '${serverId}' not found in database`,
              row: rowNum,
              field: 'serverId',
              severity: 'error',
            });
          }
        } else {
          rowWarnings.push({
            message: `Row ${rowNum}: No serverId provided. Skipping server profile creation. Use serverId to reference existing servers.`,
            row: rowNum,
            suggestion: 'Add serverId column with valid server IDs from the database',
          });
        }
      } catch (error) {
        rowErrors.push({
          message: `Failed to load server profile: ${error}`,
          row: rowNum,
          severity: 'error',
        });
      }
    }

    errors.push(...rowErrors);
    warnings.push(...rowWarnings);
  });

  // Return result
  const success = errors.filter((e) => e.severity === 'error' || e.severity === 'critical').length === 0;

  return {
    success,
    data: success ? profiles : undefined,
    errors,
    warnings,
  };
}

/**
 * Parse CSV from File object (for browser upload)
 *
 * @param file - File object from input element
 * @returns Promise with import result
 */
export async function parseCSVFile(file: File): Promise<ImportResult<ServerProfile[]>> {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target?.result as string;
      const result = parseCSV(text);
      resolve(result);
    };

    reader.onerror = () => {
      resolve({
        success: false,
        errors: [
          {
            message: `Failed to read file: ${reader.error?.message || 'Unknown error'}`,
            severity: 'critical',
          },
        ],
        warnings: [],
      });
    };

    reader.readAsText(file, 'UTF-8');
  });
}

/**
 * Generate CSV string from server profiles
 *
 * @param profiles - Array of server profiles
 * @param includeMetadata - Whether to include metadata comment rows
 * @returns CSV string
 */
export function generateCSV(profiles: ServerProfile[], includeMetadata = true): string {
  const rows: string[][] = [];

  // Add metadata as comments if requested
  if (includeMetadata) {
    rows.push([`# GPU Cluster BOM Calculator - Server Profiles`]);
    rows.push([`# Generated: ${new Date().toISOString()}`]);
    rows.push([`# Total profiles: ${profiles.length}`]);
    rows.push([``]); // Empty row for separation
  }

  // Add header row
  rows.push(CSV_HEADERS);

  // Add data rows
  profiles.forEach((profile) => {
    rows.push([
      profile.name,
      profile.vendor,
      profile.gpuType,
      String(profile.gpusPerNode),
      profile.cpuType,
      String(profile.cpuCount),
      String(profile.memoryGb),
      profile.storageType,
      String(profile.storageTb),
      String(profile.pciSlots),
      String(profile.powerWatts),
      String(profile.ruHeight),
      String(profile.ibPortsPerNode),
      String(profile.nsPortsPerNode),
      String(profile.dpusPerNode),
      String(profile.dpuOobPorts),
    ]);
  });

  // Use Papa Parse to generate CSV
  return Papa.unparse(rows, {
    quotes: true,
    delimiter: ',',
    newline: '\n',
  });
}

/**
 * Download CSV file to browser
 *
 * @param profiles - Array of server profiles
 * @param filename - Output filename
 * @param includeMetadata - Whether to include metadata
 */
export function downloadCSV(
  profiles: ServerProfile[],
  filename = 'server-profiles.csv',
  includeMetadata = true
): void {
  const csv = generateCSV(profiles, includeMetadata);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

/**
 * Validate a single server profile
 *
 * @param profile - Server profile to validate
 * @returns Array of validation errors
 */
export function validateProfile(profile: ServerProfile): ValidationError[] {
  const errors: ValidationError[] = [];

  // Required fields
  if (!profile.name || profile.name.trim().length === 0) {
    errors.push({
      field: 'name',
      message: 'Server name is required',
      severity: 'error',
    });
  }

  if (!profile.vendor || profile.vendor.trim().length === 0) {
    errors.push({
      field: 'vendor',
      message: 'Vendor is required',
      severity: 'error',
    });
  }

  if (!profile.gpuType || profile.gpuType.trim().length === 0) {
    errors.push({
      field: 'gpuType',
      message: 'GPU type is required',
      severity: 'error',
    });
  }

  // Numeric validations
  if (profile.gpusPerNode < 0) {
    errors.push({
      field: 'gpusPerNode',
      message: 'GPUs per node must be non-negative',
      severity: 'error',
    });
  }

  if (profile.powerWatts <= 0) {
    errors.push({
      field: 'powerWatts',
      message: 'Power consumption must be positive',
      severity: 'error',
    });
  }

  if (profile.ruHeight <= 0 || profile.ruHeight > 48) {
    errors.push({
      field: 'ruHeight',
      message: 'RU height must be between 1 and 48',
      severity: 'error',
    });
  }

  return errors;
}

/**
 * Generate CSV template with example server profiles from database
 *
 * @param format - 'simple' or 'detailed'
 * @param includeExamples - Whether to include example rows
 * @returns CSV string with template
 */
export function generateCSVTemplate(
  format: 'simple' | 'detailed' = 'simple',
  includeExamples = true
): string {
  const headers = format === 'simple' ? CSV_HEADERS_SIMPLE : CSV_HEADERS_DETAILED;
  const rows: string[][] = [];

  // Add header comment
  rows.push([`# GPU Cluster BOM Calculator - Server Profile Template (${format} format)`]);
  rows.push([`# Generated: ${new Date().toISOString()}`]);
  rows.push([`# Instructions: Fill in server details below or reference existing server IDs`]);
  rows.push([`# Available server IDs: ${getAllServerIds().slice(0, 5).join(', ')}...`]);
  rows.push([``]); // Empty row

  // Add header row
  rows.push(headers);

  // Add example rows if requested
  if (includeExamples) {
    const exampleServers = ALL_SERVERS.slice(0, 3);

    exampleServers.forEach((server) => {
      if (format === 'simple') {
        rows.push([
          server.id,
          server.name,
          server.vendor,
          server.model,
          server.role,
          server.gpu?.platformId || '',
          String(server.gpu?.gpusPerNode || 0),
          server.cpu.model,
          String(server.cpu.sockets),
          String(server.memory.totalGB),
          String(server.power.typicalPowerWatts),
          String(server.dimensions.rackUnits),
          server.environmental.coolingType,
          server.metadata?.notes || '',
        ]);
      } else {
        rows.push([
          server.id,
          server.name,
          server.role,
          server.vendor,
          server.model,
          server.gpu?.platformId || '',
          String(server.gpu?.gpusPerNode || 0),
          server.cpu.model,
          String(server.cpu.sockets),
          String(server.cpu.coresPerSocket),
          String(server.cpu.tdpPerSocketWatts),
          String(server.memory.totalGB),
          server.memory.type,
          String(server.memory.speedMTs),
          String(server.power.maxPowerWatts),
          String(server.power.typicalPowerWatts),
          String(server.power.idlePowerWatts),
          String(server.power.psuCount),
          String(server.power.psuCapacityWatts),
          String(server.dimensions.rackUnits),
          String(server.dimensions.widthMm),
          String(server.dimensions.depthMm),
          String(server.dimensions.heightMm),
          String(server.dimensions.weightKg),
          server.environmental.coolingType,
          String(server.environmental.tempRange.max),
          server.metadata?.notes || '',
        ]);
      }
    });
  }

  return Papa.unparse(rows, {
    quotes: true,
    delimiter: ',',
    newline: '\n',
  });
}

/**
 * Download CSV template
 *
 * @param format - 'simple' or 'detailed'
 * @param filename - Output filename
 * @param includeExamples - Whether to include example rows
 */
export function downloadCSVTemplate(
  format: 'simple' | 'detailed' = 'simple',
  filename = `server-profile-template-${format}.csv`,
  includeExamples = true
): void {
  const csv = generateCSVTemplate(format, includeExamples);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

/**
 * Export server profiles from database
 *
 * @param serverIds - Array of server IDs to export (exports all if not provided)
 * @param format - 'simple' or 'detailed'
 * @param includeMetadata - Whether to include metadata comments
 * @returns CSV string
 */
export function exportServerProfiles(
  serverIds?: string[],
  format: 'simple' | 'detailed' = 'simple',
  includeMetadata = true
): string {
  const servers = serverIds
    ? serverIds.map((id) => getServerById(id)).filter((s): s is ServerProfile => s !== undefined)
    : ALL_SERVERS;

  const headers = format === 'simple' ? CSV_HEADERS_SIMPLE : CSV_HEADERS_DETAILED;
  const rows: string[][] = [];

  if (includeMetadata) {
    rows.push([`# GPU Cluster BOM Calculator - Server Profiles Export`]);
    rows.push([`# Generated: ${new Date().toISOString()}`]);
    rows.push([`# Total profiles: ${servers.length}`]);
    rows.push([`# Format: ${format}`]);
    rows.push([``]);
  }

  // Add header row
  rows.push(headers);

  // Add data rows
  servers.forEach((server) => {
    if (format === 'simple') {
      rows.push([
        server.id,
        server.name,
        server.vendor,
        server.model,
        server.role,
        server.gpu?.platformId || '',
        String(server.gpu?.gpusPerNode || 0),
        server.cpu.model,
        String(server.cpu.sockets),
        String(server.memory.totalGB),
        String(server.power.typicalPowerWatts),
        String(server.dimensions.rackUnits),
        server.environmental.coolingType,
        server.metadata?.notes || '',
      ]);
    } else {
      rows.push([
        server.id,
        server.name,
        server.role,
        server.vendor,
        server.model,
        server.gpu?.platformId || '',
        String(server.gpu?.gpusPerNode || 0),
        server.cpu.model,
        String(server.cpu.sockets),
        String(server.cpu.coresPerSocket),
        String(server.cpu.tdpPerSocketWatts),
        String(server.memory.totalGB),
        server.memory.type,
        String(server.memory.speedMTs),
        String(server.power.maxPowerWatts),
        String(server.power.typicalPowerWatts),
        String(server.power.idlePowerWatts),
        String(server.power.psuCount),
        String(server.power.psuCapacityWatts),
        String(server.dimensions.rackUnits),
        String(server.dimensions.widthMm),
        String(server.dimensions.depthMm),
        String(server.dimensions.heightMm),
        String(server.dimensions.weightKg),
        server.environmental.coolingType,
        String(server.environmental.tempRange.max),
        server.metadata?.notes || '',
      ]);
    }
  });

  return Papa.unparse(rows, {
    quotes: true,
    delimiter: ',',
    newline: '\n',
  });
}

/**
 * Validate server ID against database
 *
 * @param serverId - Server ID to validate
 * @returns Validation result with server profile if valid
 */
export function validateServerId(serverId: string): {
  valid: boolean;
  server?: ServerProfile;
  error?: string;
} {
  if (!serverId || serverId.trim().length === 0) {
    return { valid: false, error: 'Server ID is required' };
  }

  const server = getServerById(serverId);
  if (!server) {
    return {
      valid: false,
      error: `Server ID '${serverId}' not found in database. Available IDs: ${getAllServerIds().slice(0, 10).join(', ')}...`,
    };
  }

  return { valid: true, server };
}

/**
 * Import server profiles by ID (for quick configuration)
 *
 * @param serverIds - Array of server IDs to import
 * @returns Import result with server profiles
 */
export function importServersByIds(serverIds: string[]): ImportResult<ServerProfile[]> {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];
  const profiles: ServerProfile[] = [];

  serverIds.forEach((id, index) => {
    const result = validateServerId(id);
    if (!result.valid) {
      errors.push({
        message: result.error || `Invalid server ID: ${id}`,
        row: index + 1,
        field: 'serverId',
        severity: 'error',
      });
    } else if (result.server) {
      profiles.push(result.server);
    }
  });

  return {
    success: errors.length === 0,
    data: profiles.length > 0 ? profiles : undefined,
    errors,
    warnings,
  };
}
